import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import styles from './useHideOnScroll.module.css';
import { usePageOffset } from './usePageOffset';

export const DISCLAIMER_HIDE_OFFSET = '24rem';

export const hideOnScrollClasses = {
  offset40: styles['slideUp--40'],
  offset90: styles['slideUp--90'],
  offset120: styles['slideUp--120'],
  offset320: styles['slideUp--320'],
  dynamic: styles['slideUp--dynamic'],
} as const;

export type HideOnScrollKey = keyof typeof hideOnScrollClasses;
export type HideOnScrollClass = (typeof hideOnScrollClasses)[HideOnScrollKey];

const SCROLL_COMMIT_THRESHOLD = 80;

const hasScrollHeightChanged = (scrollHeight: number, lastScrollHeight: number): boolean =>
  scrollHeight !== lastScrollHeight;

type UseHideOnScrollProps = {
  targetRef: RefObject<HTMLElement | null>;
  hideOnScrollClass: HideOnScrollClass;
  onToggleCompact?: (isCompact: boolean) => void;
  overrideHidden?: boolean;
};

export const useHideOnScroll = ({
  targetRef,
  hideOnScrollClass,
  onToggleCompact,
  overrideHidden,
}: UseHideOnScrollProps): void => {
  const location = useLocation();
  const pageOffset = usePageOffset();
  const isHiddenRef = useRef(false);
  const onToggleCompactRef = useRef(onToggleCompact);
  onToggleCompactRef.current = onToggleCompact;

  const isControlled = overrideHidden !== undefined;

  const applyVisibility = useCallback(
    (shouldHide: boolean) => {
      const targetElement = targetRef.current;
      if (!targetElement) return;
      if (shouldHide === isHiddenRef.current) return;

      isHiddenRef.current = shouldHide;
      targetElement.classList.toggle(hideOnScrollClass, shouldHide);
      if (shouldHide) {
        targetElement.style.setProperty('--hide-offset', pageOffset);
      } else {
        targetElement.style.removeProperty('--hide-offset');
      }
      onToggleCompactRef.current?.(shouldHide);
    },
    [hideOnScrollClass, targetRef, pageOffset],
  );

  useEffect(() => {
    if (overrideHidden === undefined) return;
    applyVisibility(overrideHidden);
  }, [overrideHidden, applyVisibility]);

  useEffect(() => {
    if (isControlled) return undefined;

    let highPoint = Math.max(0, window.scrollY);
    let lowPoint = highPoint;
    let ticking = false;
    let lastScrollHeight = document.documentElement.scrollHeight;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const currentScrollY = Math.max(0, window.scrollY);
        const scrollHeight = document.documentElement.scrollHeight;

        if (hasScrollHeightChanged(scrollHeight, lastScrollHeight)) {
          lastScrollHeight = scrollHeight;
          highPoint = currentScrollY;
          lowPoint = currentScrollY;
          ticking = false;
          return;
        }

        highPoint = Math.max(highPoint, currentScrollY);
        lowPoint = Math.min(lowPoint, currentScrollY);

        if (!isHiddenRef.current) {
          const downDistance = currentScrollY - lowPoint;
          if (downDistance >= SCROLL_COMMIT_THRESHOLD) {
            highPoint = currentScrollY;
            lowPoint = currentScrollY;
            applyVisibility(true);
          }
        } else {
          const upDistance = highPoint - currentScrollY;
          if (upDistance >= SCROLL_COMMIT_THRESHOLD) {
            highPoint = currentScrollY;
            lowPoint = currentScrollY;
            applyVisibility(false);
          }
        }

        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [applyVisibility, isControlled]);

  useEffect(() => {
    if (isControlled) return;
    const targetElement = targetRef.current;
    if (!targetElement) return;
    isHiddenRef.current = false;
    targetElement.classList.remove(hideOnScrollClass);
    targetElement.style.removeProperty('--hide-offset');
    onToggleCompactRef.current?.(false);
  }, [location, hideOnScrollClass, targetRef, isControlled]);
};
